import React, { useEffect, useState } from "react";
import { Modal, Avatar, Divider, Empty, Spin } from "antd";
import { CloseOutlined, MedicineBoxOutlined } from "@ant-design/icons";
import { apiGetPrescription } from "../../api/prescription";

const PrescriptionDetailsModal = ({ visible, prescription, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [prescriptionDetails, setPrescriptionDetails] = useState(null);

  useEffect(() => {
    if (visible && prescription?.id) {
      fetchPrescriptionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, prescription]);

  const fetchPrescriptionDetails = async () => {
    if (!prescription?.id) return;

    try {
      setLoading(true);
      const response = await apiGetPrescription(prescription.id);
      const data = response?.data;
      setPrescriptionDetails(data);
    } catch (error) {
      console.error("Error fetching prescription details:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!prescription) return null;

  // Get medications from API response - it's "items" not "medications"
  const medications = prescriptionDetails?.items || [];

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      centered
      footer={null}
      width={600}
      closeIcon={<CloseOutlined />}
      className="prescription-details-modal"
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Prescription Details
            </h2>

            {/* Patient Info */}
            <div className="flex items-center gap-3 mb-4">
              <Avatar
                size={48}
                src={prescription.profileImage}
                className="bg-purple-100 text-purple-600"
              >
                {prescriptionDetails?.patientName?.charAt(0) ||
                  prescription.name?.charAt(0)}
              </Avatar>
              <div>
                <div className="font-semibold text-gray-900">
                  {prescriptionDetails?.patientName || prescription.name}
                </div>
                <div className="text-sm text-gray-500">
                  Pharmacy: {prescriptionDetails?.pharmacy || "-"}
                </div>
              </div>
            </div>
          </div>

          <Divider className="my-4" />

          {/* Medications Table */}
          <div className="mb-4">
            <div className="grid grid-cols-4 gap-4 px-4 py-3 bg-gray-50 rounded-lg mb-2">
              <div className="text-xs text-gray-600 uppercase">
                Medication
              </div>
              <div className="text-xs text-gray-600 uppercase text-center">
                Dosage
              </div>
              <div className="text-xs text-gray-600 uppercase text-center">
                Duration
              </div>
              <div className="text-xs text-gray-600 uppercase text-center">
                Frequency
              </div>
            </div>

            {medications && medications.length > 0 ? (
              <div className="space-y-2">
                {medications.map((med, index) => (
                  <div
                    key={med.id || index}
                    className="grid grid-cols-4 gap-4 px-4 py-3 bg-white border border-gray-100 rounded-lg hover:border-purple-200 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
                        <MedicineBoxOutlined className="text-purple-600 text-sm" />
                      </div>
                      <span className="text-sm text-gray-900 font-medium">
                        {med.medicationName}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 text-center flex items-center justify-center">
                      {med.dosageAmount} {med.dosageUnit}
                    </div>
                    <div className="text-sm text-gray-700 text-center flex items-center justify-center">
                      {med.duration} {med.durationUnit}
                    </div>
                    <div className="text-sm text-gray-700 text-center flex items-center justify-center">
                      {med.frequency}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="No medications found" className="my-8" />
            )}
          </div>

          {/* Pricing Summary - Only show for non-premium prescriptions */}
          {prescriptionDetails && !prescriptionDetails.isPremium && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Pricing Summary</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase mb-1">Total Price</div>
                  <div className="text-lg font-semibold text-gray-900">
                    ${prescriptionDetails.totalPrice?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase mb-1">Discount</div>
                  <div className="text-lg font-semibold text-green-600">
                    -${prescriptionDetails.discountAmount?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-blue-600 uppercase mb-1">Final Price</div>
                  <div className="text-lg font-semibold text-blue-900">
                    ${prescriptionDetails.finalPrice?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default PrescriptionDetailsModal;
